import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAppointmentSlots, createAppointment, getServices, findCustomerSuggestions } from "../api";
import { AuthContext } from "../AuthContext";
import { useAlert } from "../AlertContext";
import { toISODate } from "../utils";

function CustomerChip({ customer }) {
    if (!customer) {
        return (
            <span className="inline-flex items-center px-3 py-1 text-sm rounded-full bg-gray-200 text-gray-700">
                Kein Kunde verknüpft
            </span>
        );
    }
    const label = customer.name || customer.email || customer.phone || "Unbekannter Kunde";
    const details = [customer.email, customer.phone].filter(Boolean).join(" · ");
    return (
        <Link
            to={`/customers/${customer.id}`}
            className="inline-flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 transition"
        >
            <span className="font-medium">{label}</span>
            {details && <span className="text-xs text-blue-700">{details}</span>}
        </Link>
    );
}

export default function AddAppointment() {
    const { token } = useContext(AuthContext);
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [telefon, setTelefon] = useState("");
    const [kfz, setKfz] = useState("");
    const [serviceId, setServiceId] = useState("");
    const [services, setServices] = useState([]);
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [slots, setSlots] = useState([]);

    const [customerSuggestions, setCustomerSuggestions] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [createNewCustomer, setCreateNewCustomer] = useState(true);
    const [lookupAttempted, setLookupAttempted] = useState(false);
    const [lookupStatus, setLookupStatus] = useState("idle");
    const [lookupError, setLookupError] = useState("");
    const [createdAppointment, setCreatedAppointment] = useState(null);

    const todayStr = toISODate(new Date());

    useEffect(() => {
        getServices()
            .then((data) => setServices(Array.isArray(data) ? data : []))
            .catch(() => setServices([]));
    }, []);

    useEffect(() => {
        if (!date) return;
        getAppointmentSlots(date)
            .then((data) => setSlots(Array.isArray(data) ? data : []))
            .catch(() => setSlots([]));
    }, [date]);

    const resetCustomerLookup = () => {
        setCustomerSuggestions([]);
        setSelectedCustomerId(null);
        setCreateNewCustomer(true);
        setLookupAttempted(false);
        setLookupStatus("idle");
        setLookupError("");
    };

    const handleCustomerLookup = async () => {
        const trimmedEmail = email.trim();
        const trimmedTelefon = telefon.trim();
        if (!trimmedEmail && !trimmedTelefon) {
            resetCustomerLookup();
            return;
        }
        setLookupAttempted(true);
        setLookupStatus("loading");
        setLookupError("");
        try {
            const response = await findCustomerSuggestions({
                email: trimmedEmail || null,
                telefon: trimmedTelefon || null,
            });
            const suggestions = Array.isArray(response?.suggestions) ? response.suggestions : [];
            setCustomerSuggestions(suggestions);
            if (suggestions.length > 0) {
                const existingSelection = suggestions.find((item) => item.id === selectedCustomerId);
                if (existingSelection) {
                    setSelectedCustomerId(existingSelection.id);
                } else {
                    setSelectedCustomerId(suggestions[0].id);
                }
                setCreateNewCustomer(false);
            } else {
                setSelectedCustomerId(null);
                setCreateNewCustomer(true);
            }
            setLookupStatus("loaded");
        } catch (error) {
            console.error("Customer lookup failed", error);
            setLookupStatus("error");
            setLookupError("Kundensuche derzeit nicht möglich.");
            setCustomerSuggestions([]);
            setSelectedCustomerId(null);
            setCreateNewCustomer(true);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!date || !time) {
            showAlert("Bitte Datum und Uhrzeit wählen");
            return;
        }
        const day = new Date(date).getDay();
        if (day === 0 || day === 6) {
            showAlert("Samstage und Sonntage sind nicht buchbar.");
            return;
        }
        try {
            const payload = {
                name,
                email,
                telefon,
                kfz,
                serviceId,
                date,
                time,
                createNewCustomer,
            };
            if (selectedCustomerId) {
                payload.customerId = selectedCustomerId;
            }
            const result = await createAppointment(token, payload);
            setCreatedAppointment(result);
            setName("");
            setEmail("");
            setTelefon("");
            setKfz("");
            setServiceId("");
            setDate("");
            setTime("");
            setSlots([]);
            resetCustomerLookup();
        } catch (error) {
            console.error("Failed to create appointment", error);
            showAlert("Fehler beim Anlegen");
        }
    };

    const linkType = createdAppointment?.customerLinkType
        ? createdAppointment.customerLinkType
        : createdAppointment?.customer
            ? "EXISTING_MATCHED"
            : "NONE";

    const linkTypeMessages = {
        EXISTING_SELECTED: "Bestehender Kunde wurde verknüpft.",
        EXISTING_MATCHED: "Bestehender Kunde wurde anhand der Kontaktdaten verknüpft.",
        CREATED_NEW: "Neuer Kunde wurde angelegt und verknüpft.",
        NONE: "Kein Kunde verknüpft.",
    };

    const linkTypeBadges = {
        EXISTING_SELECTED: "Bestehender Kunde",
        EXISTING_MATCHED: "Automatisch verknüpft",
        CREATED_NEW: "Neuer Kunde",
        NONE: "Ohne Kunde",
    };

    return (
        <section className="p-8">
            <h3 className="text-2xl font-bold mb-4">Termin erfassen</h3>
            <form onSubmit={handleSubmit} className="flex flex-col max-w-md space-y-4">
                <input
                    type="text"
                    placeholder="Name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    className="p-2 border rounded"
                    required
                />
                <input
                    type="email"
                    placeholder="E-Mail"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    onBlur={handleCustomerLookup}
                    className="p-2 border rounded"
                    required
                />
                <input
                    type="text"
                    placeholder="Telefon (optional)"
                    value={telefon}
                    onChange={(event) => setTelefon(event.target.value)}
                    onBlur={handleCustomerLookup}
                    className="p-2 border rounded"
                />

                {lookupAttempted && (
                    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
                        {lookupStatus === "loading" ? (
                            <p className="text-sm text-blue-700">Prüfe bestehende Kunden …</p>
                        ) : lookupStatus === "error" ? (
                            <>
                                <p className="text-sm text-red-600">{lookupError || "Kundensuche nicht möglich."}</p>
                                <label className="flex items-center gap-2 text-sm text-blue-900">
                                    <input
                                        type="checkbox"
                                        checked={createNewCustomer}
                                        onChange={(event) => setCreateNewCustomer(event.target.checked)}
                                    />
                                    <span>Neuen Kunden anlegen</span>
                                </label>
                            </>
                        ) : customerSuggestions.length > 0 ? (
                            <>
                                <p className="text-sm font-semibold text-blue-900">Bestehender Kunde gefunden?</p>
                                <div className="space-y-2">
                                    {customerSuggestions.map((suggestion) => {
                                        const infoLine = [suggestion.email, suggestion.phone]
                                            .filter(Boolean)
                                            .join(" · ");
                                        return (
                                            <label
                                                key={suggestion.id}
                                                className="flex items-start gap-2 text-sm text-blue-900 cursor-pointer"
                                            >
                                                <input
                                                    type="radio"
                                                    name="customer-selection"
                                                    value={suggestion.id}
                                                    checked={selectedCustomerId === suggestion.id}
                                                    onChange={() => {
                                                        setSelectedCustomerId(suggestion.id);
                                                        setCreateNewCustomer(false);
                                                    }}
                                                    className="mt-1"
                                                />
                                                <div>
                                                    <div className="font-medium">
                                                        {suggestion.displayName || suggestion.email || suggestion.phone || "Ohne Namen"}
                                                    </div>
                                                    {infoLine && (
                                                        <div className="text-xs text-blue-700">{infoLine}</div>
                                                    )}
                                                    {Array.isArray(suggestion.matches) && suggestion.matches.length > 0 && (
                                                        <div className="text-[11px] text-blue-600 mt-1">
                                                            Treffer über {suggestion.matches.join(" & ")}
                                                        </div>
                                                    )}
                                                </div>
                                            </label>
                                        );
                                    })}
                                    <label className="flex items-center gap-2 text-sm text-blue-900 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="customer-selection"
                                            value="new"
                                            checked={!selectedCustomerId && createNewCustomer}
                                            onChange={() => {
                                                setSelectedCustomerId(null);
                                                setCreateNewCustomer(true);
                                            }}
                                        />
                                        <span>Neuen Kunden anlegen</span>
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-blue-900 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="customer-selection"
                                            value="none"
                                            checked={!selectedCustomerId && !createNewCustomer}
                                            onChange={() => {
                                                setSelectedCustomerId(null);
                                                setCreateNewCustomer(false);
                                            }}
                                        />
                                        <span>Ohne Kundenverknüpfung</span>
                                    </label>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-sm text-blue-900">Kein bestehender Kunde gefunden.</p>
                                <label className="flex items-center gap-2 text-sm text-blue-900">
                                    <input
                                        type="checkbox"
                                        checked={createNewCustomer}
                                        onChange={(event) => setCreateNewCustomer(event.target.checked)}
                                    />
                                    <span>Neuen Kunden anlegen</span>
                                </label>
                            </>
                        )}
                    </div>
                )}

                <input
                    type="text"
                    placeholder="Kennzeichen"
                    value={kfz}
                    onChange={(event) => setKfz(event.target.value)}
                    className="p-2 border rounded"
                    required
                />
                <select
                    value={serviceId}
                    onChange={(event) => setServiceId(event.target.value)}
                    className="p-2 border rounded"
                    required
                >
                    <option value="">-- Service wählen --</option>
                    {services.map((service) => (
                        <option key={service.id} value={service.id}>
                            {service.name}
                        </option>
                    ))}
                </select>
                <input
                    type="date"
                    value={date}
                    min={todayStr}
                    onChange={(event) => {
                        const value = event.target.value;
                        const weekday = new Date(value).getDay();
                        if (weekday === 0 || weekday === 6) {
                            showAlert("Samstage und Sonntage sind nicht buchbar.");
                            event.target.value = "";
                            setDate("");
                            return;
                        }
                        setDate(value);
                    }}
                    className="p-2 border rounded"
                    required
                />
                <select
                    value={time}
                    onChange={(event) => setTime(event.target.value)}
                    className="p-2 border rounded"
                    required
                >
                    <option value="">-- Uhrzeit --</option>
                    {slots.map((slot) => (
                        <option key={slot.time} value={slot.time} disabled={slot.booked}>
                            {slot.time}
                            {slot.booked ? " (belegt)" : ""}
                        </option>
                    ))}
                </select>
                <button type="submit" className="bg-blue-600 text-white p-2 rounded">
                    Speichern
                </button>
            </form>

            {createdAppointment && (
                <div className="mt-6 space-y-3 rounded-lg border border-green-200 bg-green-50 p-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-green-800">Termin gespeichert</span>
                        <span className="rounded-full bg-white/70 px-2 py-0.5 text-xs text-green-700">
                            {createdAppointment.date} · {createdAppointment.time}
                        </span>
                        <span className="rounded-full bg-green-200 px-2 py-0.5 text-xs text-green-800">
                            {linkTypeBadges[linkType] || linkTypeBadges.NONE}
                        </span>
                    </div>
                    <p className="text-sm text-green-900">{linkTypeMessages[linkType] || linkTypeMessages.NONE}</p>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-green-900">Kunde:</span>
                        <CustomerChip customer={createdAppointment.customer} />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-green-900">Fahrzeug:</span>
                        {createdAppointment.vehicle ? (
                            <span className="inline-flex items-center gap-2 rounded-full bg-white/70 px-3 py-1 text-sm text-green-800">
                                <span>{createdAppointment.vehicle.licensePlate || "Unbekanntes Kennzeichen"}</span>
                                <span className="text-xs text-green-700">#{createdAppointment.vehicle.id}</span>
                            </span>
                        ) : (
                            <span className="text-sm text-green-900">Kein Fahrzeug verknüpft</span>
                        )}
                    </div>
                    <div className="flex flex-wrap gap-3 pt-1">
                        <button
                            type="button"
                            onClick={() => navigate("/appointments")}
                            className="rounded border border-green-400 px-3 py-1.5 text-sm font-medium text-green-800 hover:bg-green-100 transition"
                        >
                            Zur Terminübersicht
                        </button>
                    </div>
                </div>
            )}
        </section>
    );
}
